import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import { Box, Stack, useTheme } from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '@verantos-dev/shared-components';

import { FeedbackSnackbar } from '../../admin-common/components/FeedbackSnackbar';
import { useSaveProjectEditsMutation } from '../api/adminProjectsApiSlice';
import { useEditProject } from '../context/EditProjectContext';
import type { SaveProjectEditsRequest } from '../types/types';

export const EditProjectControls = () => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const {
    editMode,
    hasChanges,
    isEditBlocked,
    projectId,
    enterEditMode,
    exitEditMode,
    cancelEdits,
    addedAnnotators,
    removedAnnotatorIds,
    addedSupervisors,
    removedSupervisorIds,
    confirmedReallocations,
  } = useEditProject();

  const [saveProjectEdits, { isLoading }] = useSaveProjectEditsMutation();
  const [snackbar, setSnackbar] = useState<{
    severity: 'success' | 'error';
    message: string;
  } | null>(null);

  const handleSave = async () => {
    const body: SaveProjectEditsRequest = {
      addedAnnotatorIds: addedAnnotators.map((a) => a.id),
      removedAnnotatorIds,
      addedSupervisorIds: addedSupervisors.map((s) => s.id),
      removedSupervisorIds,
      reallocations: confirmedReallocations.map(
        ({ batch, fromAnnotatorId, toAnnotatorId, count }) => ({
          batch,
          fromAnnotatorId,
          toAnnotatorId,
          count,
        })
      ),
    };

    try {
      await saveProjectEdits({ projectId, ...body }).unwrap();
      setSnackbar({
        severity: 'success',
        message: formatMessage({ id: 'adminProjects.projectDetails.saveChanges.success' }),
      });
      exitEditMode();
    } catch (err: any) {
      console.error(err);
      const message =
        err?.data?.detail ||
        formatMessage({ id: 'adminProjects.projectDetails.saveChanges.error' });
      setSnackbar({ severity: 'error', message });
    }
  };

  return (
    <Box>
      {editMode ? (
        <Stack direction="row" spacing={2}>
          <Button
            id="cancel-edit-project-button"
            variant="outlined"
            onClickHandler={cancelEdits}
            disabled={isLoading}
            displayLabel={formatMessage({ id: 'adminProjects.projectDetails.cancel' })}
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              color: theme.palette.primary.main,
            }}
          />
          <Button
            id="save-edit-changes-button"
            variant="contained"
            startIcon={<CheckIcon />}
            onClickHandler={handleSave}
            disabled={!hasChanges || isLoading}
            displayLabel={formatMessage({
              id: isLoading
                ? 'adminProjects.projectDetails.saveChanges.loading'
                : 'adminProjects.projectDetails.saveChanges',
            })}
            sx={{
              textTransform: 'none',
              fontSize: '14px',
              backgroundColor: theme.palette.primary.main,
            }}
          />
        </Stack>
      ) : (
        !isEditBlocked && (
          <Button
            id="edit-project-button"
            variant="contained"
            startIcon={<EditIcon />}
            onClickHandler={enterEditMode}
            displayLabel={formatMessage({ id: 'adminProjects.projectDetails.editProject' })}
            sx={{
              px: theme.spacing(3),
              textTransform: 'none',
              fontSize: '14px',
              backgroundColor: theme.palette.primary.main,
            }}
          />
        )
      )}
      <FeedbackSnackbar
        open={Boolean(snackbar)}
        severity={snackbar?.severity}
        message={snackbar?.message ?? ''}
        onClose={() => setSnackbar(null)}
      />
    </Box>
  );
};
